//import {gsap, ScrollToPlugin, InertiaPlugin, Draggable} from 'gsap/all';

import {dataset, lazyCustomElements, listen} from 'nuffjs';

const conf = {
	groupAttr: 'group',
	itemsAttr: 'items',
	animatingClass: 'animating',
};

let gsap = null;
let Draggable = null;
async function preloadLibraries() {
	if (!gsap) {
		const lib = await import('../../external/gsap');
		gsap = lib.gsap;
		Draggable = lib.Draggable;
	}
}

const observerCallbacks = [];
const observer = new IntersectionObserver(
	(entries) => {
		for (const callback of observerCallbacks) {
			callback(entries);
		}
	},
	{
		root: document,
		rootMargin: '0px 0px 0px 0px',
		threshold: [0],
	}
);

const marqueesMixin = (Base) =>
	class extends Base {
		static conf = conf;

		static defaultOpts = {
			baseSpeed: 10,
			randomSpeed: 20,
		};

		isPaused = true;
		intersecting = false;
		allowedToPlay = false;
		items = [];
		speeds = [];
		positions = [];
		draggables = [];

		initializeAnimationProperties() {
			this.speeds = [];
			this.positions = [];
			this.items = [];
			this.draggables = [];
		}

		firstConnectedCallback() {
			this.opts = dataset(this).get(this.constructor.defaultOpts);
			this.groups = this.querySelectorAll(dataset.selector(conf.groupAttr));
			this.groupsAmount = this.groups.length;
			this.initializeAnimationProperties();

			preloadLibraries().then(async () => {
				this.initAnimation();
				this.sizes = [];
				this.checkSizes();
				observerCallbacks.push(this.onIntersect.bind(this));
				observer.observe(this);

				listen(window, 'resize', this.checkSizes.bind(this), {throttle: true});

				const animationsToggler = await lazyCustomElements.querySelector(
					document.body,
					'[is="app-animation-toggler"]'
				);
				if (animationsToggler) {
					listen(
						document.body,
						animationsToggler.getEventName(),
						this.onAnimationsToggle.bind(this)
					);
					this.allowedToPlay = !animationsToggler.isPaused();
					this.updatePlayback();
				}
			});
		}

		initAnimation() {
			for (let i = 0; i < this.groupsAmount; i++) {
				this.speeds[i] = this.opts.baseSpeed + this.opts.randomSpeed * Math.random();
				this.positions[i] = 0;
				this.items[i] = this.groups[i].querySelector(dataset.selector(conf.itemsAttr));

				this.draggables[i] = Draggable.create(this.groups[i], {
					type: 'x',
					bounds: this,
					lockAxis: true,
					inertia: true,
				});
			}
		}

		updatePlayback() {
			if (this.isPaused && this.allowedToPlay && this.intersecting) {
				this.isPaused = false;
				requestAnimationFrame(this.animate.bind(this));
			} else {
				this.isPaused = true;
				this.previousTimeStamp = null;
			}
		}

		onIntersect(entries) {
			for (const entry of entries) {
				if (entry.target === this) {
					this.intersecting = !!entry.isIntersecting;
					this.updatePlayback();
					break;
				}
			}
		}

		onAnimationsToggle(event) {
			this.allowedToPlay = !event.detail.isPaused;
			this.updatePlayback();
		}

		animate(timestamp) {
			if (this.isPaused || !this.allowedToPlay || !this.intersecting) {
				return;
			}
			let elapsed = 0;
			if (this.previousTimeStamp) {
				elapsed = timestamp - this.previousTimeStamp;
			}

			if (elapsed) {
				for (let i = 0; i < this.groupsAmount; i++) {
					this.positions[i] =
						this.getTranslateValues(this.groups[i]).x - this.speeds[i] * Math.abs(elapsed) * 0.001;
					//isThrowing can be false or undefined;
					if (this.positions[i] < -this.sizes[i] && this.draggables[i].isThrowing !== true) {
						this.positions[i] += this.sizes[i];
					}
					// z coordinate + transform-style: preserve-3d allow a smooth movement in firefox
					gsap.set(this.groups[i], {x: this.positions[i], z: 0.01});
				}
			}

			this.previousTimeStamp = timestamp;

			requestAnimationFrame(this.animate.bind(this));
		}

		checkSizes() {
			for (let i = 0; i < this.groupsAmount; i++) {
				this.sizes[i] = this.items[i].scrollWidth;
			}
		}

		getTranslateValues(element) {
			const style = window.getComputedStyle(element);
			const matrix = style.transform;

			// No transform property. Simply return 0 values.
			if (matrix === 'none' || typeof matrix === 'undefined') {
				return {
					x: 0,
					y: 0,
					z: 0,
				};
			}

			// Can either be 2d or 3d transform
			const matrixType = matrix.includes('3d') ? '3d' : '2d';
			const matrixValues = matrix.match(/matrix.*\((.+)\)/)[1].split(', ');

			// 2d matrices have 6 values
			// Last 2 values are X and Y.
			// 2d matrices does not have Z value.
			if (matrixType === '2d') {
				return {
					x: matrixValues[4],
					y: matrixValues[5],
					z: 0,
				};
			} else {
				return {
					x: matrixValues[12],
					y: matrixValues[13],
					z: matrixValues[14],
				};
			}
		}
	};

export {marqueesMixin};
